/**
 * Structured-data date fields for a page.
 *
 * Standing rule: editing a page means reviewing it that day, so the review
 * date and the edit date are one value — the page's own content date in
 * `route-lastmod.json`. That is the same value the visible "Last reviewed and
 * edited on:" line and the sitemap `<lastmod>` publish, so readers, crawlers
 * and the JSON-LD never disagree.
 *
 * `fallback` is only for pages the registry does not cover yet; it keeps the
 * two fields equal instead of letting one drift.
 */
import { lastmodFor } from "./route-lastmod";

export function schemaDateFields(
  path: string,
  fallback?: string,
): { lastReviewed?: string; dateModified?: string } {
  const iso = lastmodFor(path) ?? fallback;
  return iso ? { lastReviewed: iso, dateModified: iso } : {};
}

/** Same fields, from a full canonical URL. */
export function schemaDateFieldsFromUrl(
  url: string,
  fallback?: string,
): { lastReviewed?: string; dateModified?: string } {
  return schemaDateFields(new URL(url).pathname, fallback);
}

/**
 * Edit date only. `lastReviewed` is a WebPage property, so Blog and
 * CollectionPage nodes carry just `dateModified` — still the same page
 * content date the visible line and the sitemap publish.
 */
export function schemaEditDate(
  path: string,
  fallback?: string,
): { dateModified?: string } {
  const iso = lastmodFor(path) ?? fallback;
  return iso ? { dateModified: iso } : {};
}

/**
 * Google rejects datetimes without a timezone offset (Search Console:
 * "Invalid datetime value"). News post metadata stores local times like
 * "2026-08-10T00:00:00" with no zone, so schema and article meta emit the
 * date part only — a plain ISO date is valid and matches what the page
 * visibly shows. Values already carrying a zone (Z or ±hh:mm) pass through.
 */
export function schemaIsoDate(value: string): string {
  const m = value.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2})?$/);
  return m ? m[1] : value;
}

/**
 * Google's ProfilePage rich result requires `dateModified` as a full
 * DateTime with a timezone offset; a bare date is reported as "Invalid
 * datetime value". Expands a node's date-only `dateModified` to midnight
 * Eastern (offset computed for that date, so EST/EDT stay correct).
 * `lastReviewed` stays a plain date, matching the visible review line.
 */
export function profileDateTime(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const probe = new Date(`${iso}T12:00:00Z`);
  const tz = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    timeZoneName: "longOffset",
  })
    .formatToParts(probe)
    .find((p) => p.type === "timeZoneName")?.value;
  const offset = tz?.replace("GMT", "") || "-05:00";
  return `${iso}T00:00:00${offset}`;
}

export function withProfileDateTime<T extends { dateModified?: string }>(node: T): T {
  return node.dateModified
    ? { ...node, dateModified: profileDateTime(node.dateModified) }
    : node;
}
